import { Ring } from '@uiball/loaders'
import OperatorImage from 'assets/images/Operator.png'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import { Modal } from 'components/UI/Modal'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { useGenerateQRLink } from 'hooks/useGenerateQRLink'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import { useQ19CallMutation } from 'redux/api/creditApi'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { WCCallEventProps, WCVideoCall } from 'share/d.ts/q19'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw from 'twin.macro'
import { getWsStepStatus, isStepHaveData } from 'utils'
import { q19CallTopic } from 'utils/constants'
import Emitter from 'utils/emitter'

type Q19CallStepData = {
  call: { id: string; datetime: string }
  user: {
    id: number
    iin: string
    phone: string
    external_id: string
    external_token: string | null
  }
  status: 'rejected' | 'tech_error' | 'success'
  comment: string | null
  operator: { id: number; username: string }
  tech_error_reason: string | null
}

const Q19CallPage = () => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { Q19_CALL } = useAppSelector(getNotificationsSelector)

  // State
  const [isCondOpen, setIsCondOpen] = useState(false)
  const [showQ19Call, setShowQ19Call] = useState(false)
  const [isCallEnded, setIsCallEnded] = useState(false)

  // Hooks
  const qrValue = useGenerateQRLink()

  // Mutations
  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [startQ19Call, { data, isLoading: isQ19CallLoading, isSuccess: isQ19CallSuccess }] = useQ19CallMutation()

  // Computed values
  const isQ19CallDataFetched = isQ19CallSuccess && data

  // Handlers
  const handleQ19CallStart = async () => {
    try {
      setIsCondOpen(false)
      await fetchQ19CallData()
    } catch (error) {
      console.log(error)
    }
  }

  const fetchQ19CallData = async () => {
    try {
      if (!currentFlowId) throw new Error('Flow id not found')
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) throw new Error('Current step not found')
      if (current_step.step !== FLOW_STEPS.Q19_CALL) throw new Error('Current step is not Q19_CALL')
      await startQ19Call(current_step.id).unwrap()
      setShowQ19Call(true)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (!showQ19Call) return
    const videoCallElem = document.querySelector<WCVideoCall>('wc-video-call')
    if (!videoCallElem) return console.error('wc-video-call element not found')

    const videoCallListener = (event: CustomEvent<WCCallEventProps>) => {
      if (!event.detail || !event.detail.length) return
      const callData = event.detail[0].payload.data
      if ('state' in callData && callData.state === 'ended') {
        setShowQ19Call(false)
        setIsCallEnded(true)
      }
    }

    videoCallElem.addEventListener('wc', videoCallListener)
    return () => {
      videoCallElem.removeEventListener('wc', videoCallListener)
    }
  }, [showQ19Call])

  useEffect(() => {
    const wsStepStatus = getWsStepStatus(Q19_CALL)
    if (wsStepStatus === 'IN_PROGRESS' && isStepHaveData<Q19CallStepData>(Q19_CALL?.value.data)) {
      const callStatus = Q19_CALL.value.data.status
      if (callStatus === 'tech_error') {
        setShowQ19Call(false)
        setIsCallEnded(false)
      }
    } else {
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    }
  }, [Q19_CALL, isCallEnded])

  if (!isCallEnded && isQ19CallDataFetched && showQ19Call) {
    return (
      <div id='video-call-wrapper'>
        <wc-video-call
          id='wc-video-call'
          height='calc(100vh - 88px)'
          topic={q19CallTopic}
          host-name='https://cc.bankffin.kz'
          domain={window.location.origin}
          user-id={data.customer_id}
          lang={language}
          request-call-feedback={0}
          end-screen-new-call-button={1}
          extra={JSON.stringify({
            product: data.product,
            principal: data.principal,
            period: data.period,
            organisation_name: data.organisation_name
          })}
        />
      </div>
    )
  }

  return (
    <ContainerWithSteps>
      {isCallEnded ? (
        <div tw='flex flex-col'>
          <Heading variant='title2' twStyle={tw`text-center`}>
            {t('flow-please-wait')}!
          </Heading>
          <div tw='flex items-center justify-center my-auto'>
            <Ring size={80} color='var(--primary-color)' />
          </div>
        </div>
      ) : (
        <div>
          <InfoBlock twStyle={tw`flex flex-col items-center relative pt-[135px] md:pt-5 dark:bg-[#202020]`}>
            <img src={OperatorImage} alt='operator' />
            <Heading as='h3' variant='subtitle' twStyle={tw`max-w-[200px] text-center mt-6`}>
              {t('q19-block.title')}
            </Heading>
            <Typography variant='subbody' twStyle={tw`mt-3 text-center text-grey-dark`}>
              {t('q19-block.text')}
            </Typography>
            <Button
              variant='primary'
              twStyle={tw`mt-[88px] md:mt-6`}
              loading={isQ19CallLoading || isStatusLoading || isCallEnded}
              onClick={() => setIsCondOpen(true)}
            >
              <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
                {t('q19-block.btn-text')}
              </Typography>
            </Button>
          </InfoBlock>
          <InfoBlock twStyle={tw`hidden sm:flex items-center sm:justify-center flex-wrap lg:flex-nowrap gap-6 mt-6`}>
            <div tw='space-y-3'>
              <Typography variant='subbody' weight='bold' twStyle={tw`font-semibold max-w-[25em]`}>
                {t('flow-no-camera')}
              </Typography>
              <ul tw='list-disc ml-5 text-[#5A5A5A] dark:text-grey-light'>
                <li>
                  <Typography as='span' variant='subbody' text>
                    {t('flow-open-camera')}
                  </Typography>
                </li>
                <li>
                  <Typography as='span' variant='subbody' text>
                    {t('flow-screen-qr')}
                  </Typography>
                </li>
                <li>
                  <Typography as='span' variant='subbody' text>
                    {t('flow-go-to-site')}
                  </Typography>
                </li>
                <li>
                  <Typography as='span' variant='subbody' text>
                    {t('flow-identify')}
                  </Typography>
                </li>
              </ul>
            </div>
            <QRCodeSVG className='qr-code bvi-no-styles' size={160} value={qrValue} />
          </InfoBlock>
          <Modal
            title={t('flow-be-sure')}
            open={isCondOpen}
            onClose={() => setIsCondOpen(false)}
            headerStyle={tw`px-5 md:px-0`}
            twStyle={tw` py-5 px-0 md:px-5`}
          >
            <ol tw='mt-6 list-decimal list-inside space-y-4 px-5 md:px-0 bg-secondary pt-5 md:pt-0 pb-4 md:pb-0'>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-1')}
                </Typography>
              </li>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-2')}
                </Typography>
              </li>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-3')}
                </Typography>
              </li>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-4')}
                </Typography>
              </li>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-5')}
                </Typography>
              </li>
              <li>
                <Typography variant='body' as='span' text>
                  {t('flow-be-sure-6')}
                </Typography>
              </li>
            </ol>
            <Button twStyle={tw`mx-auto mt-5 md:mt-8 w-full`} variant='secondary' onClick={handleQ19CallStart}>
              {t('flow-begin')}
            </Button>
          </Modal>
        </div>
      )}
    </ContainerWithSteps>
  )
}

export default Q19CallPage
